import { useQuery } from "@tanstack/react-query";

/* ---------- Open-Meteo Weather (for non-Florida locations) ---------- */

export interface OpenMeteoWeatherResponse {
  current: {
    temperature_2m: number; // °C
    wind_speed_10m: number; // km/h
    wind_direction_10m: number; // degrees
    wind_gusts_10m?: number; // km/h — newly requested, optional for old cached responses
    weather_code: number;
  };
  hourly?: {
    time: string[];
    temperature_2m: number[];
    wind_speed_10m: number[];
    wind_direction_10m: number[];
    wind_gusts_10m: number[];
    weather_code: number[];
  };
  daily: {
    time: string[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    weather_code: number[];
    wind_speed_10m_max: number[];
    wind_direction_10m_dominant: number[];
    sunrise?: string[];
    sunset?: string[];
  };
}

export interface HourlyWind {
  time: string;
  tempC: number;
  windSpeedKmh: number;
  windGustsKmh: number;
  windDirectionDeg: number;
  weatherCode: number;
}

/**
 * Find the wind reading closest in time to `targetIso`. Returns null for
 * empty input. Callers should prefer an exact Map lookup first and fall
 * back here only when marine hourly and wind hourly diverge (which happens
 * across DST boundaries and when providers round to different minutes).
 */
export function findNearestWind(
  targetIso: string,
  hourlyWind: HourlyWind[],
): HourlyWind | null {
  if (hourlyWind.length === 0) return null;
  const t = new Date(targetIso).getTime();
  let closest = hourlyWind[0];
  let minDiff = Math.abs(new Date(closest.time).getTime() - t);
  for (let i = 1; i < hourlyWind.length; i++) {
    const diff = Math.abs(new Date(hourlyWind[i].time).getTime() - t);
    if (diff < minDiff) {
      minDiff = diff;
      closest = hourlyWind[i];
    }
  }
  return closest;
}

export interface OpenMeteoWeather {
  currentTemp: number; // °C
  currentWindSpeed: number; // km/h
  currentWindDirection: number; // degrees
  /** Peak gust in km/h; null when the upstream response lacks it. */
  currentWindGusts: number | null;
  currentWeatherCode: number;
  hourlyWind: HourlyWind[];
  daily: {
    date: string;
    tempMax: number;
    tempMin: number;
    weatherCode: number;
    windSpeedMax: number;
    windDirectionDominant: number;
    sunrise: string | null;
    sunset: string | null;
  }[];
}

const WEATHER_CODE_LABELS: Record<number, string> = {
  0: "Clear sky",
  1: "Mainly clear",
  2: "Partly cloudy",
  3: "Overcast",
  45: "Fog",
  48: "Rime fog",
  51: "Light drizzle",
  53: "Moderate drizzle",
  55: "Dense drizzle",
  56: "Freezing drizzle",
  57: "Dense freezing drizzle",
  61: "Slight rain",
  63: "Moderate rain",
  65: "Heavy rain",
  66: "Freezing rain",
  67: "Heavy freezing rain",
  71: "Slight snow",
  73: "Moderate snow",
  75: "Heavy snow",
  77: "Snow grains",
  80: "Slight showers",
  81: "Moderate showers",
  82: "Violent showers",
  85: "Slight snow showers",
  86: "Heavy snow showers",
  95: "Thunderstorm",
  96: "Thunderstorm + hail",
  99: "Thunderstorm + heavy hail",
};

export function getWeatherCodeLabel(code: number): string {
  return WEATHER_CODE_LABELS[code] ?? "Unknown";
}

export function getWeatherCodeEmoji(code: number): string {
  if (code === 0) return "☀️";
  if (code <= 3) return "⛅";
  if (code <= 48) return "🌫️";
  if (code <= 57) return "🌦️";
  if (code <= 67) return "🌧️";
  if (code <= 77) return "🌨️";
  if (code <= 82) return "🌧️";
  if (code <= 86) return "🌨️"; // snow showers (codes 83-86)
  if (code >= 95) return "⛈️";
  return "🌤️";
}

async function fetchOpenMeteoWeather(
  lat: number,
  lon: number,
): Promise<OpenMeteoWeatherResponse> {
  const url = `/api/forecast?lat=${lat}&lon=${lon}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Forecast API error ${res.status}`);
  return res.json();
}

function transformOpenMeteoWeather(raw: OpenMeteoWeatherResponse): OpenMeteoWeather {
  // Optional chaining per-field: cached responses from before we added
  // `wind_gusts_10m` / `weather_code` to the request can return an
  // `hourly` object that's missing those arrays. A bare `[i]` access
  // then crashed the render.
  const h = raw.hourly;
  const hourlyWind: HourlyWind[] = h
    ? h.time.map((time, i) => ({
        time,
        tempC: h.temperature_2m?.[i] ?? 0,
        windSpeedKmh: h.wind_speed_10m?.[i] ?? 0,
        windGustsKmh: h.wind_gusts_10m?.[i] ?? 0,
        windDirectionDeg: h.wind_direction_10m?.[i] ?? 0,
        weatherCode: h.weather_code?.[i] ?? 0,
      }))
    : [];

  return {
    currentTemp: raw.current.temperature_2m,
    currentWindSpeed: raw.current.wind_speed_10m,
    currentWindDirection: raw.current.wind_direction_10m,
    currentWindGusts: raw.current.wind_gusts_10m ?? null,
    currentWeatherCode: raw.current.weather_code,
    hourlyWind,
    daily: raw.daily.time.map((date, i) => ({
      date,
      tempMax: raw.daily.temperature_2m_max[i],
      tempMin: raw.daily.temperature_2m_min[i],
      weatherCode: raw.daily.weather_code[i],
      windSpeedMax: raw.daily.wind_speed_10m_max[i],
      windDirectionDominant: raw.daily.wind_direction_10m_dominant[i],
      sunrise: raw.daily.sunrise?.[i] ?? null,
      sunset: raw.daily.sunset?.[i] ?? null,
    })),
  };
}

export function useOpenMeteoWeather(lat: number | null, lon: number | null) {
  return useQuery({
    queryKey: ["open-meteo-weather", lat, lon],
    queryFn: () => fetchOpenMeteoWeather(lat!, lon!),
    enabled: lat !== null && lon !== null,
    select: transformOpenMeteoWeather,
    staleTime: 15 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
    retry: 2,
  });
}
